/**
 * The pages of one board, and what a teacher may do to them (SRS C1).
 *
 * A board is one or more pages, each an infinite canvas of its own. This file is the
 * list of them and the four operations on it — add, rename, duplicate, delete — plus
 * dragging one into a different place in the strip.
 *
 * The list lives in the shared document, so a page added in Lahore appears on every
 * screen in the class without anybody asking for it (SRS C6).
 */

import {
  addPage,
  duplicatePage,
  getBackground,
  getPages,
  getPageSummaries,
  findPage,
  movePage,
  PAGE_COUNT_WARNING_THRESHOLD,
  removePage,
  renamePage,
  setPageBackground,
  type PageBackground,
} from '@opal/board-doc';
import type { PageSummary, Unsubscribe } from '@opal/shared';
import type * as Y from 'yjs';

export { PAGE_COUNT_WARNING_THRESHOLD };

/** SRS C1 — unlimited pages, with a warning once a board has this many. */
export function tooManyPages(count: number): boolean {
  return count > PAGE_COUNT_WARNING_THRESHOLD;
}

export interface PageBook {
  /** Every page, in the order the tabs are in. */
  list(): readonly PageSummary[];
  /** Calls back whenever a page is added, renamed, moved or taken away. */
  onChanged(handler: (pages: readonly PageSummary[]) => void): Unsubscribe;
  /** Adds an empty page at the end and returns its id. */
  add(): string;
  rename(pageId: string, name: string): void;
  /** Copies a page, work and all, and puts it next to the original. */
  duplicate(pageId: string): string | null;
  /** Takes a page away. False when it is the only one the board has. */
  remove(pageId: string): boolean;
  /** Drags a page to another place in the strip. */
  move(pageId: string, toIndex: number): boolean;
  /** SRS C7 — the paper this page is drawn on. */
  background(pageId: string): PageBackground | null;
  setBackground(pageId: string, patch: Partial<PageBackground>): void;
  dispose(): void;
}

/**
 * Wraps a board document as the list of its pages.
 *
 * `origin` is what marks a change as this person's own, so turning a page or renaming
 * one lands on their undo pile and nobody else's (SRS C6).
 */
export function createPageBook(doc: Y.Doc, origin: unknown): PageBook {
  const handlers = new Set<(pages: readonly PageSummary[]) => void>();
  const pages = getPages(doc);

  const announce = (): void => {
    const summaries = getPageSummaries(doc);
    for (const handler of handlers) handler(summaries);
  };

  // Deep, because renaming a page changes what is inside the page rather than the
  // list holding it, and the tab strip has to hear about both.
  pages.observeDeep(announce);

  return {
    list: () => getPageSummaries(doc),

    onChanged(handler) {
      handlers.add(handler);
      handler(getPageSummaries(doc));
      return () => {
        handlers.delete(handler);
      };
    },

    add: () => addPage(doc, undefined, origin),
    rename: (pageId, name) => {
      renamePage(doc, pageId, name, origin);
    },
    duplicate: (pageId) => duplicatePage(doc, pageId, origin),
    remove: (pageId) => removePage(doc, pageId, origin),
    move: (pageId, toIndex) => movePage(doc, pageId, toIndex, origin),

    background(pageId) {
      const page = findPage(doc, pageId);
      return page ? getBackground(page) : null;
    },
    setBackground(pageId, patch) {
      if (!findPage(doc, pageId)) return;
      setPageBackground(doc, pageId, patch, origin);
    },

    dispose() {
      pages.unobserveDeep(announce);
      handlers.clear();
    },
  };
}

/**
 * The page to turn to when the one being worked on is taken away.
 *
 * The page that took its place, or the one before it when the last page went — the
 * same thing a browser does when a tab closes, and what the hand is already near.
 */
export function pageAfterRemoval(
  pages: readonly PageSummary[],
  removedIndex: number,
): string | null {
  const next = pages[Math.min(removedIndex, pages.length - 1)];
  return next?.id ?? null;
}
